import type { ShapeVertex } from './gardenReducer';

export const STORAGE_KEY = 'garden-management:v2';
export const SCHEMA_VERSION = 2;

// Default size for a newly created garden shape. The canvas itself is an
// infinite, freely pannable plane (like Figma) — this only seeds the initial
// rectangle's dimensions; the shape can be dragged/resized anywhere from there.
export const CANVAS_WIDTH = 900;
export const CANVAS_HEIGHT = 600;

export const MIN_ELEMENT_SIZE = 20;

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 5;
export const ZOOM_STEP = 0.25;
export const DEFAULT_ZOOM = 1;

// How strongly Ctrl/Cmd+scroll (and trackpad pinch) changes zoom per unit of
// wheel deltaY; used as `zoom * exp(-deltaY * WHEEL_ZOOM_SENSITIVITY)`.
export const WHEEL_ZOOM_SENSITIVITY = 0.0025;

// Screen-pixel movement beyond which a pointer-down+move is treated as a
// drag (move/pan) rather than a click/tap.
export const DRAG_THRESHOLD_PX = 4;

// Distance (px) within which a click on the shape boundary while editing
// counts as "on an edge" and inserts a new vertex there.
export const EDGE_INSERT_THRESHOLD = 18;

export type ElementCategory = 'plant' | 'structure';

export interface ElementMeta {
  type: string;
  label: string;
  category: ElementCategory;
  resizable?: boolean;
  defaultWidth?: number;
  defaultHeight?: number;
}

export const ELEMENT_CATEGORIES: Record<ElementCategory, string> = {
  plant: 'Plants',
  structure: 'Structures',
};

export const ELEMENT_TYPES: ElementMeta[] = [
  { type: 'tree', label: 'Tree', category: 'plant' },
  { type: 'bush', label: 'Bush', category: 'plant' },
  { type: 'flower', label: 'Flower', category: 'plant' },
  { type: 'shrub', label: 'Shrub', category: 'plant' },
  { type: 'vegetablePatch', label: 'Vegetable patch', category: 'plant' },
  { type: 'pathRock', label: 'Path / rock', category: 'plant' },
  {
    type: 'building',
    label: 'Building',
    category: 'structure',
    resizable: true,
    defaultWidth: 70,
    defaultHeight: 60,
  },
  {
    type: 'lake',
    label: 'Lake',
    category: 'structure',
    resizable: true,
    defaultWidth: 120,
    defaultHeight: 80,
  },
];

export const ELEMENT_META_BY_TYPE: Record<string, ElementMeta> = ELEMENT_TYPES.reduce<
  Record<string, ElementMeta>
>((acc, meta) => {
  acc[meta.type] = meta;
  return acc;
}, {});

export const ELEMENT_LABEL_BY_TYPE: Record<string, string> = ELEMENT_TYPES.reduce<
  Record<string, string>
>((acc, { type, label }) => {
  acc[type] = label;
  return acc;
}, {});

export function defaultShape(): ShapeVertex[] {
  return [
    { id: 'corner-1', x: 0, y: 0 },
    { id: 'corner-2', x: CANVAS_WIDTH, y: 0 },
    { id: 'corner-3', x: CANVAS_WIDTH, y: CANVAS_HEIGHT },
    { id: 'corner-4', x: 0, y: CANVAS_HEIGHT },
  ];
}
